// Putting a named point on the chart, by either of the two routes onto it:
// look the name up (shared place database first, geocoder on a miss --
// places.mjs), or arm the point and let the user tap where it goes.
//
// The tap used to be reachable only as the geocoder's consolation prize -- a
// miss armed the point, and nothing else could. But a private dock, a mooring
// buoy or an anchorage is a name no geocoder will ever know, and a point placed
// in the wrong spot needs re-placing without a rename first. So the two are
// peers now ("Lookup place" and "Place on map"), and `armPlacement` is the
// direct writer for the same queue a miss fills.
//
// This used to live inside TripRouteMap, where it only ever handled the trip's
// two endpoints. It sits here now because the waypoint list -- which renders in
// the eagerly-loaded page, above the lazy map chunk -- offers both buttons
// per row: every point can be unplaced (a rename invalidating a geocoded
// position, an import, a geocode miss), so every point needs a way back onto
// the map. Both editors drive this one hook, so a tap can't be claimed twice
// and the pending-placement hint says the same thing wherever it's read.
//
// A target is:
//   { id }    an existing waypoint (placed or not) -- geocode its own name
//   { kind }  an anchor that isn't in the route at all (a trip that has never
//             been located) -- geocode the trip's From/To column
//
// A queued target is answered by ONE of two taps, and which one it was matters:
// placeAtTap takes the coordinates under the finger, placeAtPlace takes a
// gazetteer ROW the finger landed on. See the note above placeAtPlace -- they
// differ in a good deal more than where the numbers come from.
//
// Nothing here saves; positions flow out through onRouteChange, and the trip
// page's autosave owns persistence.

import { useCallback, useMemo, useRef, useState } from 'react';
import NearbyPlacesModal from '../../components/NearbyPlacesModal';
import { normalizePlaceName, nearbyPlaces } from '../../fleet-shared/PlaceFields.mjs';
import {
  locatePlaceName,
  recordManualPlacement,
  recordPickedPlace,
} from '../../utilities/places.mjs';
import {
  ANCHOR_COPY,
  bindWaypointToPlace,
  makeWaypoint,
  placeWaypoint,
  setAnchorFromPlace,
  upsertAnchor,
} from '../../fleet-shared/TripRoute.mjs';

const VIA_NOUN = 'that waypoint';
const VIA_HINT = 'Tap the map to place this waypoint';

export function targetKey(target) {
  return target?.id ? `id:${target.id}` : `kind:${target?.kind}`;
}

function waypointFor(route, target) {
  if (!target?.id) return null;
  return (route?.waypoints || []).find((wp) => wp.id === target.id) || null;
}

// The kind a target refers to, whether it names a waypoint or an absent anchor.
function kindFor(route, target) {
  return target?.kind || waypointFor(route, target)?.kind || null;
}

// The tap-to-place badge for whichever target is waiting on a tap.
export function placementHint(route, target) {
  const kind = kindFor(route, target);
  return ANCHOR_COPY[kind]?.hint || VIA_HINT;
}

// What to say about the points that didn't land, from `{ name, renamed }` per
// miss. The two reasons need different words: the geocoder genuinely not
// knowing a name is worth telling the user (try another one), while a name
// that changed mid-lookup was never actually looked up -- "couldn't find
// Tacoma" would send them off rewording a name that may be perfectly findable.
function missNotice(misses) {
  const notFound = misses.filter((miss) => !miss.renamed).map((miss) => miss.name);
  const renamedCount = misses.length - notFound.length;

  const parts = [];
  if (notFound.length > 0) parts.push(`couldn't find ${notFound.join(' and ')}`);
  if (renamedCount > 0) {
    parts.push(`the name${renamedCount > 1 ? 's' : ''} changed while the lookup ran`);
  }

  const reason = parts.join('; ');
  return `${reason.charAt(0).toUpperCase()}${reason.slice(1)} — tap the map to place ${
    misses.length > 1 ? 'them' : 'it'
  }.`;
}

// Why a manual placement didn't reach the shared row, in the words the user
// needs. Both name the place, because "a curated place" means nothing to
// someone who has never seen the gazetteer -- this is the first surface on the
// trip side that mentions the concept at all.
//
// The failed-write wording commits to the consequence rather than softening
// it. A bound waypoint takes its row's position on the next read, so a
// writeback that didn't land really will move the pin back, and saying "your
// change wasn't shared" would leave the user to discover that themselves.
function placementRejectionNotice(reason, place) {
  const name = (place?.name || '').trim();
  const subject = name ? `“${name}”` : 'That place';

  if (reason === 'curated') {
    return `${subject} is a curated place, so its position on the map didn't change. Edit it under Places to move it for everyone.`;
  }
  return `Couldn't update the shared place for ${subject}. Your pin will move back the next time this trip loads — try dragging it again.`;
}

export function useRouteLocator({ route, routeRef, onRouteChange, endpointName, placeIndexRef }) {
  // Targets waiting for a map tap, in the order they'll consume taps.
  const [pendingTargets, setPendingTargets] = useState([]);
  // Which targets have a geocode in flight, by key -- the row that was clicked
  // says "Locating…" rather than every button on the page.
  const [locatingKeys, setLocatingKeys] = useState([]);
  const [notice, setNotice] = useState(null);
  // Deliberately NOT the same state as `notice`. That one belongs to the
  // locate/tap cycle -- locate() clears it, a drained queue clears it, and its
  // copy always ends "tap the map to place it". A placement refusal outlives
  // all of that and reads nothing like it.
  const [placementNotice, setPlacementNotice] = useState(null);
  // Questions waiting on the "there is already a place here" dialog, in the
  // order they were asked. A queue rather than one slot because `locate` is
  // multi-target: "Look up all" can geocode a whole route and come back with
  // several names that each sit next to something. One dialog walked through
  // them beats N stacked dialogs or one compound one nobody can read.
  const [nearbyQueue, setNearbyQueue] = useState([]);
  // How many of THIS run's questions have been answered, for the "2 of 3"
  // line. Counted rather than derived from the queue, which only knows what is
  // left. Resets when the queue drains.
  const [nearbyAnswered, setNearbyAnswered] = useState(0);

  // Read through a ref, not the closure. `locate` runs across an await, and on
  // the far side it has to compare against the name the point has NOW -- a
  // captured `endpointName` would still be reporting the column as it was when
  // the button was clicked, which is exactly the value the check exists to
  // catch changing. (The route side is already a ref for the same reason.)
  const endpointNameRef = useRef(endpointName);
  endpointNameRef.current = endpointName;

  const nameFor = useCallback(
    (target) => {
      const current = routeRef.current;
      if (target?.id) return waypointFor(current, target)?.name || '';
      return endpointNameRef.current(target?.kind) || '';
    },
    [routeRef]
  );

  // An endpoint the user never named is described, never referred to by its
  // raw kind ("Couldn't find the destination", not "Couldn't find to").
  const describe = useCallback(
    (target) => {
      const name = nameFor(target).trim();
      if (name) return name;
      const kind = kindFor(routeRef.current, target);
      return ANCHOR_COPY[kind]?.noun || VIA_NOUN;
    },
    [nameFor, routeRef]
  );

  // Fold one position into `base`. An existing waypoint keeps its id and name
  // and is simply placed; an anchor that isn't in the route yet is created from
  // the trip's column (upsertAnchor keeps `from` first and `to` last, and
  // leaves the vias alone).
  // The async backstop, wired into recordManualPlacement as `onRejected`. What
  // the synchronous guard below can't know (a row curated since this page
  // loaded) and what nothing could know in advance (the write failed) both
  // arrive here.
  const reportPlacementRejected = useCallback((reason, place) => {
    setPlacementNotice(placementRejectionNotice(reason, place));
  }, []);

  // May this waypoint's position be moved by hand? False only for a point
  // bound to a CURATED row: that row is ground truth, recordManualPlacement
  // refuses to overwrite it, and -- since a bound waypoint takes its row's
  // position on every read -- letting the drag through would show the user a
  // pin that quietly jumps back on the next load. Refusing up front, with a
  // sentence, is the honest version of the same policy.
  //
  // Synchronous on purpose: `curated` rides along in the loaded index (it is in
  // the place selection set), so this costs no round trip and the marker never
  // visibly moves before being told no.
  //
  // No index means NO INFORMATION, so it allows the placement -- the same
  // fail-open reasoning resolveRouteAgainstPlaces uses for an empty index.
  // Blocking edits because a background fetch hasn't landed would be far worse
  // than the rare late refusal reportPlacementRejected then handles.
  const allowManualPlacement = useCallback(
    (waypoint) => {
      const row = waypoint?.placeId
        ? placeIndexRef?.current?.byId?.get(waypoint.placeId)
        : null;
      if (!row?.curated) return true;
      setPlacementNotice(placementRejectionNotice('curated', row));
      return false;
    },
    [placeIndexRef]
  );

  // Is this about to become a duplicate? Synchronous, off the same loaded index
  // allowManualPlacement reads, so the common answer ("no") costs one array
  // walk and no round trip.
  //
  // No index means NO INFORMATION and returns [] -- the same fail-open the
  // guard above takes, and the same one resolveRouteAgainstPlaces takes for an
  // empty index. A background fetch that hasn't landed must not turn into a
  // dialog, and creating the row is what this code did before it existed.
  //
  // The name is excluded by KEY: an exact match is already a no-op inside
  // recordPickedPlace, so asking about one would interrupt a gesture that was
  // never going to write anything.
  const nearbyTo = useCallback(
    (point, name) => {
      const byId = placeIndexRef?.current?.byId;
      if (!(byId instanceof Map) || byId.size === 0) return [];
      return nearbyPlaces(point, [...byId.values()], { excludeNameKey: name });
    },
    [placeIndexRef]
  );

  // Put "there is already a place here" to the user, or don't bother them.
  //
  // `onCreate` runs SYNCHRONOUSLY when there is nothing nearby, which is what
  // keeps the ordinary path exactly as it was -- no dialog, no state change, no
  // extra render. Only a real near-miss reaches the queue.
  //
  // `onUse(place)` binds to the row instead. Its callers re-read the route
  // through routeRef when it fires: the dialog is modeless in the sense that
  // matters here -- typing continues behind it, and the waypoint may have been
  // renamed or deleted between the ask and the answer.
  const confirmNewPlace = useCallback(
    ({ point, name, onUse, onCreate }) => {
      const matches = nearbyTo(point, name);
      if (matches.length === 0) {
        onCreate?.();
        return;
      }
      setNearbyQueue((current) => [...current, { point, name, matches, onUse, onCreate }]);
    },
    [nearbyTo]
  );

  const nearbyHead = nearbyQueue[0] || null;

  // The question already answered, by object identity. A ref and not state,
  // for useCopyLegToLogbook's reason: state is last render's, so a second
  // click in the same tick would read the old value and answer twice -- and
  // here answering twice means calling `onCreate` twice, which writes exactly
  // the duplicate row this whole dialog exists to prevent.
  const answeredRef = useRef(null);

  // Every exit from the dialog drops the head of the queue; they differ only in
  // what they do on the way out. Dismissing does NOTHING -- no row, no
  // rebinding -- because of the two outcomes, the one that isn't written is the
  // one that can still be changed by hand afterwards.
  //
  // The side effect runs OUTSIDE the state updater. React may call an updater
  // more than once for one dispatch, so a write in there is a write that can
  // happen twice.
  //
  // Memoized like every other function on the returned object: it is a dep of
  // the modal element below, which is a dep of the hook's own useMemo, so a
  // fresh identity here would rebuild that object on every render.
  const answerNearby = useCallback(
    (act) => {
      if (!nearbyHead || answeredRef.current === nearbyHead) return;
      answeredRef.current = nearbyHead;
      act?.(nearbyHead);
      const rest = nearbyQueue.slice(1);
      setNearbyQueue(rest);
      // Back to zero once the run is over, so the NEXT batch counts from one
      // rather than continuing this one's tally.
      setNearbyAnswered(rest.length === 0 ? 0 : nearbyAnswered + 1);
    },
    [nearbyHead, nearbyQueue, nearbyAnswered]
  );

  // "2 of 3", and it has to be derived rather than read off the queue: the
  // queue holds what is LEFT, so `1 of ${queue.length}` counts down (3, 2, 1)
  // and tells the reader the one thing they already know -- that this is the
  // next one. Answered-plus-remaining is the total, and it stays put while the
  // position advances. A batch that grows mid-run (a second lookup started
  // behind the dialog) simply raises the total, which is the honest answer.
  const nearbyTotal = nearbyAnswered + nearbyQueue.length;

  // An ELEMENT, not a component (see the returned object below) -- and one
  // built inside a useMemo, because a JSX element rebuilt on every render is a
  // dep that changes on every render, which would defeat the hook's own memo
  // and hand every consumer a new `locator` each time the page rendered.
  const nearbyModal = useMemo(
    () => (
      <NearbyPlacesModal
        show={Boolean(nearbyHead)}
        places={nearbyHead?.matches || []}
        progress={
          nearbyTotal > 1
            ? `${nearbyAnswered + 1} of ${nearbyTotal} names to confirm`
            : undefined
        }
        onUse={(place) => answerNearby((head) => head.onUse?.(place))}
        onCreate={() => answerNearby((head) => head.onCreate?.())}
        onHide={() => answerNearby(null)}
      />
    ),
    [nearbyHead, nearbyAnswered, nearbyTotal, answerNearby]
  );

  // `placeId` is the gazetteer row the position came from, when there was one:
  // locatePlaceName returns it for a stored row and null for a fresh geocoder
  // hit -- which is also the marker that this name is not in the gazetteer yet
  // and somebody has to decide whether it should be (see `locate`).
  // Binding here is what makes a located point follow the place database
  // afterwards rather than keeping the copy it was given.
  const applyPosition = useCallback(
    (base, target, { lat, lon, placeId }, geocoded) => {
      const src = geocoded ? 'geocode' : 'user';
      const geocodedAt = geocoded ? new Date().toISOString() : undefined;
      if (waypointFor(base, target)) {
        return placeWaypoint(base, target.id, {
          lat,
          lon,
          src,
          geocodedAt,
          placeId: placeId || undefined,
        });
      }
      const kind = target?.kind;
      // A {id} target whose waypoint vanished (deleted while the geocode was
      // in flight) has nowhere to land.
      if (!kind) return base;
      return upsertAnchor(
        base,
        makeWaypoint({
          kind,
          name: nameFor(target),
          lat,
          lon,
          src,
          geocodedAt,
          placeId: placeId || undefined,
        })
      );
    },
    [nameFor]
  );

  // Hand the next map tap (or taps) to these targets, with no lookup at all.
  // This is "Place on map": the user already knows where the point goes, and
  // the queue it fills is the same one a geocode miss fills, so every consumer
  // -- the badge, the row hint, placeAtTap's ordering -- works unchanged.
  //
  // Appends rather than replaces, so arming Start and then End queues both taps
  // in the order they were asked for; the dedupe by key makes a second click on
  // an already-armed row a no-op rather than a second tap to spend.
  //
  // A curated-bound point is refused here rather than at the tap. The refusal
  // is the same policy allowManualPlacement enforces for a drag, and it sets
  // its own notice -- so the button explains itself immediately instead of
  // arming a placement that the tap would then decline.
  const armPlacement = useCallback(
    (targets) => {
      const list = (Array.isArray(targets) ? targets : [targets]).filter(Boolean);
      const base = routeRef.current;
      const allowed = list.filter((target) => allowManualPlacement(waypointFor(base, target)));
      if (allowed.length === 0) return;
      setNotice(null);
      setPendingTargets((current) => {
        const keys = new Set(current.map(targetKey));
        const added = allowed.filter((target) => !keys.has(targetKey(target)));
        return added.length > 0 ? [...current, ...added] : current;
      });
    },
    [routeRef, allowManualPlacement]
  );

  // "Use the existing place" -- point a target at a gazetteer ROW instead of at
  // the coordinates a geocoder guessed for it.
  //
  // This is placeAtPlace's gesture reached from a dialog rather than a tap, and
  // it obeys the same contract: the waypoint is BOUND, not copied. Name
  // verbatim, position, placeId and mapLabel all come off the row, which is
  // what makes resolveRouteAgainstPlaces hand back the identical object on the
  // next read -- if the name were massaged here, opening the page would become
  // a mutation. Nothing is written to the gazetteer: the row already says what
  // the user just confirmed.
  //
  // The route is re-read through routeRef because a dialog is answered whenever
  // the user gets round to it, and typing carries on behind it. `expectedName`
  // is the name the question was asked ABOUT -- a target that has since been
  // renamed is answering a question nobody is asking any more (`locate`'s own
  // rule), and a waypoint that has been deleted has nowhere to land.
  const bindTargetToPlace = useCallback(
    (target, place, expectedName) => {
      const base = routeRef.current;
      const kind = kindFor(base, target);
      // A {kind} target may or may not have a waypoint by now: `locate` asks
      // about anchors the route has never carried, while TripWaypoints' anchor
      // pick creates one before it asks. Resolve by id first, then by kind, so
      // both land on the same point.
      const existing =
        waypointFor(base, target) ||
        (kind ? (base?.waypoints || []).find((wp) => wp.kind === kind) : null);

      // Compare against the point itself when there is one, and only fall back
      // to the trip's From/To column when there isn't -- the column is mirrored
      // FROM the route a render later, so reading it here would judge a fresh
      // anchor pick against the previous name.
      const currentName = existing ? existing.name : nameFor(target);
      if (normalizePlaceName(currentName) !== normalizePlaceName(expectedName)) return;

      // setAnchorFromPlace refuses a kind that isn't an anchor and hands `base`
      // straight back, so a via with no waypoint simply does nothing.
      const next = existing
        ? bindWaypointToPlace(base, existing.id, place)
        : setAnchorFromPlace(base, kind, place);
      if (next !== base) onRouteChange(next);
    },
    [routeRef, onRouteChange, nameFor]
  );

  // Take a target back out of the queue -- arming is a deliberate click now, so
  // it needs an exit that isn't "tap the map somewhere and undo it".
  const cancelPlacement = useCallback(
    (target) => {
      const key = targetKey(target);
      const rest = pendingTargets.filter((t) => targetKey(t) !== key);
      if (rest.length === pendingTargets.length) return;
      setPendingTargets(rest);
      // Same rule placeAtTap ends on: the notice always finishes "tap the map
      // to place it", so an empty queue makes it a description of the past.
      if (rest.length === 0) setNotice(null);
    },
    [pendingTargets]
  );

  const locate = useCallback(
    async (targets) => {
      const list = (Array.isArray(targets) ? targets : [targets]).filter(Boolean);
      if (list.length === 0) return;
      const keys = list.map(targetKey);

      setLocatingKeys((current) => [...current, ...keys]);
      setNotice(null);
      try {
        // The exact names sent to the geocoder, kept so each result can be
        // matched back to the name it actually describes.
        const queries = list.map(nameFor);
        const positions = await Promise.all(queries.map(locatePlaceName));

        // Re-read the route AFTER the await rather than using this render's
        // prop: a geocode can take seconds, and the map stays interactive
        // throughout (only the buttons disable), so a marker dragged meanwhile
        // would be clobbered by a merge onto the stale snapshot.
        const base = routeRef.current;
        let next = base;
        const missing = [];
        // Names the geocoder answered that the gazetteer has never heard of.
        // locatePlaceName no longer stores these itself, so each one is a
        // decision: a new row, or a binding to something already there.
        // Collected here and settled below, AFTER the route edit -- the point
        // goes on the map either way, and only the shared-database write is in
        // question.
        const unknown = [];
        list.forEach((target, index) => {
          // Only the button disables while a lookup runs -- the name box stays
          // editable -- so the name can change under us. A geocoded position
          // belongs to the name it was looked up FROM (nameOwnsPosition), and
          // placing this one now would stamp `src: 'geocode'` on coordinates
          // for a name the point no longer has: a pin at the old place that
          // the app believes describes the new name, and that a later rename
          // would dutifully invalidate while this one silently didn't. The
          // result is for a question nobody is asking any more -- drop it and
          // treat the target as unfound.
          const renamed = nameFor(target).trim() !== queries[index].trim();
          if (positions[index] && !renamed) {
            next = applyPosition(next, target, positions[index], true);
            // A null placeId means the answer came from the geocoder rather
            // than a stored row -- see locatePlaceName. A renamed target never
            // reaches here, so it now stores nothing at all; it used to create
            // a row for a name the user had already abandoned.
            if (!positions[index].placeId) {
              unknown.push({ target, name: queries[index], position: positions[index] });
            }
          } else {
            missing.push({ target, renamed });
          }
        });

        if (next !== base) onRouteChange(next);

        unknown.forEach(({ target, name, position }) => {
          confirmNewPlace({
            point: position,
            name,
            onCreate: () => recordPickedPlace({ name, lat: position.lat, lon: position.lon }),
            onUse: (place) => bindTargetToPlace(target, place, name),
          });
        });
        // Only this lookup's targets are re-answered: one that landed leaves
        // the queue, one that missed joins it, and anything armed by hand or
        // missed by an earlier lookup stays exactly where it was. (Replacing
        // the whole queue would drop a "Place on map" the user armed while
        // this geocode was in flight.)
        setPendingTargets((current) => [
          ...current.filter((target) => !keys.includes(targetKey(target))),
          ...missing.map((miss) => miss.target),
        ]);
        if (missing.length > 0) {
          setNotice(missNotice(missing.map((miss) => ({ ...miss, name: describe(miss.target) }))));
        }
      } finally {
        setLocatingKeys((current) => current.filter((key) => !keys.includes(key)));
      }
    },
    [
      nameFor,
      describe,
      applyPosition,
      onRouteChange,
      routeRef,
      confirmNewPlace,
      bindTargetToPlace,
    ]
  );

  // A map tap while something is waiting to be placed. Returns whether it
  // consumed the tap, so the map can fall through to add-waypoint mode.
  const placeAtTap = useCallback(
    (lat, lon) => {
      const [target, ...rest] = pendingTargets;
      if (!target) return false;
      const base = routeRef.current;
      const existing = waypointFor(base, target);

      // Same refusal the marker drag gets, for the same reason: a curated row
      // won't accept the position, so placing the point would only show a pin
      // that jumps back. The tap is still CONSUMED (return true) -- the target
      // stays queued and the notice explains why nothing moved, which beats
      // falling through to add-waypoint mode and dropping a stray via where
      // the user was trying to correct an existing point.
      if (!allowManualPlacement(existing)) return true;

      // A tap is a person asserting where this name really is -- feed it back
      // to the shared place database so the next lookup skips the geocoder
      // that just failed. Fire-and-forget; placement doesn't wait on it.
      //
      // The binding rides through both calls: the tap corrects the row this
      // point is bound to (rather than whichever row shares its name), and the
      // point stays bound to it afterwards.
      recordManualPlacement({
        name: nameFor(target),
        autoName: existing?.autoName,
        lat,
        lon,
        placeId: existing?.placeId,
        onRejected: reportPlacementRejected,
      });
      const next = applyPosition(base, target, { lat, lon, placeId: existing?.placeId }, false);
      if (next !== base) onRouteChange(next);
      setPendingTargets(rest);
      // The notice only ever says "tap the map to place it"; once nothing is
      // waiting it's describing the past.
      if (rest.length === 0) setNotice(null);
      return true;
    },
    [
      pendingTargets,
      nameFor,
      applyPosition,
      onRouteChange,
      routeRef,
      allowManualPlacement,
      reportPlacementRejected,
    ]
  );

  // A map tap that landed on a gazetteer DOT while something is waiting to be
  // placed. Sibling of placeAtTap, same return contract (true = the tap was
  // consumed, so the map doesn't fall through to add-waypoint mode), and the
  // difference between them is everything: this tap is not a claim about
  // coordinates, it is a claim about a ROW. So the target is BOUND -- name,
  // position, placeId and map label all off the row -- and the gazetteer goes
  // on owning where it is.
  //
  // No recordManualPlacement. placeAtTap writes back because a bare tap is new
  // information about where a name is; here the row already owns the position
  // and the user picked it precisely because it is right. A writeback would at
  // best re-send a row its own values, and at worst refuse on a curated row the
  // user is perfectly entitled to point at, and then explain a failure that
  // never happened.
  //
  // No allowManualPlacement either, for the same reason. Both of that guard's
  // justifications evaporate: nothing is written, so nothing can be refused;
  // and "a bound waypoint takes its row's position on every read" -- the
  // mechanism that makes a refused drag show a pin that jumps back -- is here
  // the DESIRED outcome, since the row it will read from is the one the user
  // just chose. This is in fact the only gesture that moves a waypoint off a
  // wrong curated binding without renaming it first.
  //
  // The ROW's name wins over the trip's From/To column for a {kind} target,
  // deliberately unlike applyPosition's nameFor(): the user pointed at a
  // specific place, and calling it by the column's spelling would leave a
  // waypoint bound to a row it disagrees with -- which the very next resolve
  // would rename anyway. The trip page mirrors an anchor's name back into the
  // column, so the column follows within the same edit.
  const placeAtPlace = useCallback(
    (place) => {
      const [target, ...rest] = pendingTargets;
      if (!target) return false;
      const base = routeRef.current;
      const existing = waypointFor(base, target);

      // A {id} target whose waypoint vanished (deleted while the point was
      // armed) has nowhere to land -- applyPosition's rule, restated because
      // this path doesn't go through it.
      const next = existing
        ? bindWaypointToPlace(base, target.id, place)
        : (target?.kind ? setAnchorFromPlace(base, target.kind, place) : base);

      if (next !== base) onRouteChange(next);
      setPendingTargets(rest);
      // Same rule placeAtTap ends on: the notice only ever says "tap the map to
      // place it", so an empty queue makes it a description of the past.
      if (rest.length === 0) setNotice(null);
      return true;
    },
    [pendingTargets, onRouteChange, routeRef]
  );

  const isLocating = useCallback(
    (target) => locatingKeys.includes(targetKey(target)),
    [locatingKeys]
  );

  const isPending = useCallback(
    (target) => pendingTargets.some((t) => targetKey(t) === targetKey(target)),
    [pendingTargets]
  );

  return useMemo(
    () => ({
      pendingTargets,
      pendingHint: pendingTargets.length > 0 ? placementHint(route, pendingTargets[0]) : null,
      locating: locatingKeys.length > 0,
      isLocating,
      isPending,
      notice,
      dismissNotice: () => setNotice(null),
      // The "already a place here" dialog, as an ELEMENT rather than a
      // component -- useCopyLegToLogbook's rule. A component identity rebuilt
      // when a dep changes remounts what it renders, and Bootstrap's Modal
      // would tear down and re-enter mid-question.
      //
      // One instance, mounted by the page. Two would let two dialogs open at
      // once over one queue.
      nearbyModal,
      confirmNewPlace,
      bindTargetToPlace,
      locate,
      armPlacement,
      cancelPlacement,
      placeAtTap,
      placeAtPlace,
      allowManualPlacement,
      reportPlacementRejected,
      placementNotice,
      dismissPlacementNotice: () => setPlacementNotice(null),
    }),
    [
      pendingTargets,
      route,
      locatingKeys,
      isLocating,
      isPending,
      notice,
      nearbyModal,
      confirmNewPlace,
      bindTargetToPlace,
      locate,
      armPlacement,
      cancelPlacement,
      placeAtTap,
      placeAtPlace,
      allowManualPlacement,
      reportPlacementRejected,
      placementNotice,
    ]
  );
}

export default useRouteLocator;
